<template>
  <div class="w-full h-full flex flex-col overflow-y-auto">
    <DashboardHeader title="My Referrals" link="/my-referrals">
      <template #right>
        <MyReferralsFilters
          :application-status="myReferralsStore.applicationStatusFilter"
          :interview-status="myReferralsStore.interviewStatusFilter"
          :referral-status="myReferralsStore.referralStatusFilter"
          :timeframe="myReferralsStore.timeframeFilter"
          :submitted="myReferralsStore.submittedFilter"
          :statistics="myReferralsStore.statistics"
          @update:application-status="onApplicationStatusChange"
          @update:interview-status="onInterviewStatusChange"
          @update:referral-status="onReferralStatusChange"
          @update:timeframe="onTimeframeChange"
          @toggle-pending="onTogglePending"
          @toggle-submitted="onToggleSubmitted"
          @select-all="onSelectAll"
        />
      </template>
    </DashboardHeader>

    <div
      v-if="myReferralsStore.error"
      class="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700"
    >
      {{ myReferralsStore.error }}
    </div>

    <div class="flex w-full flex-1 overflow-hidden bg-white border border-gray-200 rounded-xl">
      <MyReferralsTable
        :applications="myReferralsStore.applications"
        :loading="myReferralsStore.isLoading"
        :current-page="myReferralsStore.currentPage"
        :total-pages="totalPages"
        :application-expanded="applicationExpanded"
        class="flex-[3]"
        :class="{ 'hidden xl:block': applicationExpanded }"
        @update-referral-status="handleUpdateReferralStatus"
        @update-page="handlePageChange"
      />

      <router-view
        v-if="applicationExpanded"
        class="w-full flex-[5]"
        @refresh-requested="handleRefreshRequested"
      />
    </div>

    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="searchQuery"
        placeholder="Search referrals..."
        @search="handleSearch"
        class="w-full"
      />
    </Teleport>

    <Teleport to="#topbar-right" defer>
      <button
        class="text-xs text-black/80 bg-lime-400 rounded-md py-2 px-4 hover:bg-lime-500 transition-all duration-200"
        @click="showLeaderboard = true"
      >
        Show leaderboard
      </button>
    </Teleport>

    <Dialog v-model:open="showLeaderboard">
      <DialogContent class="max-w-2xl p-0 overflow-hidden">
        <DialogHeader class="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle class="flex items-center gap-2 text-lg font-normal text-gray-900">
            <Trophy class="h-5 w-5 text-amber-500" />
            Referral Leaderboard
          </DialogTitle>
          <DialogDescription class="text-xs text-gray-500 flex flex-col gap-2">
            <span>Top referrers ranked by total and successful referrals.</span>
            <span class="text-xs text-gray-500">* Referral counts reflect submissions recorded from <strong>October 22, 2025</strong>, following a system update that enabled referral confirmation tracking</span>
          </DialogDescription>
        </DialogHeader>
        <div
          v-if="myReferralsStore.leaderboardCurrentUser"
          class="mx-6 flex items-center justify-between rounded-lg bg-lime-50 border border-lime-200 px-4 py-3"
        >
          <span class="text-sm text-gray-700">Your confirmed referrals</span>
          <span class="text-lg font-semibold text-emerald-600">
            {{ myReferralsStore.leaderboardCurrentUser.successful_referrals }}
          </span>
        </div>
        <div class="max-h-[60vh] overflow-y-auto">
          <MyReferralsLeaderboard />
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Trophy } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import MyReferralsFilters from '@/components/myReferrals/MyReferralsFilters.vue'
import MyReferralsLeaderboard from '@/components/myReferrals/MyReferralsLeaderboard.vue'
import MyReferralsTable from '@/components/myReferrals/MyReferralsTable.vue'
import { useMyReferralsStore } from '@/stores/myReferrals'
import type { ReferrerAllowedStatus } from '@/types/referrerDashboard'
import SearchInput from '@/components/common/SearchInput.vue'

const myReferralsStore = useMyReferralsStore()
const { toast } = useToast()
const route = useRoute()
const router = useRouter()

const totalPages = computed(() => {
  const size = myReferralsStore.pageSize || 20
  return Math.max(Math.ceil(myReferralsStore.totalCount / size), 1)
})
const applicationExpanded = computed(() => route.params.application_id !== undefined)
const searchQuery = ref('')
const showLeaderboard = ref(false)

async function refreshApplications(page = 1) {
  await myReferralsStore.applyFilters(page)
}

async function onApplicationStatusChange(value: string) {
  myReferralsStore.applicationStatusFilter = value
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function onInterviewStatusChange(value: string) {
  myReferralsStore.interviewStatusFilter = value
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function onReferralStatusChange(value: string) {
  myReferralsStore.referralStatusFilter = value
  // Clear timeframe when manually changing referral status via dropdown
  if (value !== 'OUTTALENT_REQUESTED_REFERRAL') {
    myReferralsStore.timeframeFilter = ''
  }
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function onTimeframeChange(value: string) {
  myReferralsStore.timeframeFilter = value
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function onTogglePending() {
  const isCurrentlyPending =
    myReferralsStore.referralStatusFilter === 'OUTTALENT_REQUESTED_REFERRAL'
  if (isCurrentlyPending) {
    myReferralsStore.referralStatusFilter = ''
    myReferralsStore.timeframeFilter = ''
  } else {
    myReferralsStore.referralStatusFilter = 'OUTTALENT_REQUESTED_REFERRAL'
    myReferralsStore.timeframeFilter = '30'
    myReferralsStore.submittedFilter = false
  }
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function onToggleSubmitted() {
  if (myReferralsStore.submittedFilter) {
    myReferralsStore.submittedFilter = false
  } else {
    myReferralsStore.submittedFilter = true
    myReferralsStore.referralStatusFilter = ''
    myReferralsStore.timeframeFilter = ''
  }
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function handleSearch() {
  myReferralsStore.searchFilter = searchQuery.value
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function onSelectAll() {
  myReferralsStore.clearFilters()
  searchQuery.value = ''
  await refreshApplications(1)
  await router.replace({ query: { ...route.query, page: '1' } })
}

async function handlePageChange(page: number) {
  await router.push({ query: { ...route.query, page: String(page) } })
}

async function handleUpdateReferralStatus(applicationId: number, status: ReferrerAllowedStatus) {
  try {
    await myReferralsStore.updateReferralStatus(applicationId, status)
    toast({
      title: 'Referral status updated',
      description: 'Your referral status has been updated successfully.'
    })
  } catch {
    toast({
      title: 'Update failed',
      description: 'Unable to update referral status.',
      variant: 'destructive'
    })
  }
}

async function handleRefreshRequested() {
  await refreshApplications(myReferralsStore.currentPage)
}

onMounted(async () => {
  const pageFromUrl = Number(route.query.page || 1)
  const initialPage = Number.isNaN(pageFromUrl) || pageFromUrl < 1 ? 1 : pageFromUrl
  await Promise.all([
    myReferralsStore.fetchMyStatistics(),
    myReferralsStore.applyFilters(initialPage)
  ])
})

watch(
  () => route.query.page,
  async (newPage, oldPage) => {
    if (newPage === oldPage) return
    const parsed = Number(newPage || 1)
    const page = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed
    if (page !== myReferralsStore.currentPage) {
      await refreshApplications(page)
    }
  }
)
</script>
